import { DEV_TOOLS_STORAGE_KEY } from './constants';
import type { MfeVersionsType } from './hooks/useMfeVersions';

export type DevToolsPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export const POSITION_OPTIONS: { value: DevToolsPosition; label: string }[] = [
	{ value: 'bottom-left', label: 'Bottom Left' },
	{ value: 'bottom-right', label: 'Bottom Right' },
	{ value: 'top-left', label: 'Top Left' },
	{ value: 'top-right', label: 'Top Right' }
];

export interface DevToolsPreferences {
	showDevTools: boolean;
	position: DevToolsPosition;
}

export interface DevToolsStorageType {
	data: {
		mfeVersions: MfeVersionsType;
	};
	preferences: DevToolsPreferences;
}

const DEFAULT_PREFERENCES: DevToolsPreferences = {
	showDevTools: true,
	position: 'bottom-left'
};

const DEFAULT_MFE_VERSIONS: MfeVersionsType = {
	auth: { version: '' },
	garage: { version: '' },
	settings: { version: '' }
};

/**
 * Validates if the given object matches the MfeVersionsType structure
 * @param obj - Object to validate
 * @returns True if valid, false otherwise
 */
const isValidMfeVersionsType = (obj: unknown): obj is MfeVersionsType => {
	if (!obj || typeof obj !== 'object') {
		return false;
	}

	const versions = obj as Record<string, unknown>;
	const requiredKeys = ['auth', 'garage', 'settings'];

	return requiredKeys.every((key) => {
		const mfeVersion = versions[key];
		return (
			mfeVersion &&
			typeof mfeVersion === 'object' &&
			'version' in mfeVersion &&
			typeof mfeVersion.version === 'string'
		);
	});
};

const VALID_POSITIONS: DevToolsPosition[] = [
	'top-left',
	'top-right',
	'bottom-left',
	'bottom-right'
];

/**
 * Validates if the given object matches the DevToolsStorageType structure
 * @param obj - Object to validate
 * @returns True if valid, false otherwise
 */
const isValidDevToolsStorage = (obj: unknown): obj is DevToolsStorageType => {
	if (!obj || typeof obj !== 'object') {
		return false;
	}

	const storage = obj as Record<string, unknown>;

	// Check data structure
	if (!storage.data || typeof storage.data !== 'object') {
		return false;
	}

	const data = storage.data as Record<string, unknown>;
	if (!isValidMfeVersionsType(data.mfeVersions)) {
		return false;
	}

	// Check preferences structure
	if (!storage.preferences || typeof storage.preferences !== 'object') {
		return false;
	}

	const preferences = storage.preferences as Record<string, unknown>;
	if (typeof preferences.showDevTools !== 'boolean') {
		return false;
	}

	// Position is optional for backwards compatibility - validate if present
	if (
		preferences.position !== undefined &&
		!VALID_POSITIONS.includes(preferences.position as DevToolsPosition)
	) {
		return false;
	}

	return true;
};

/**
 * Gets the full storage object from sessionStorage
 * @returns The full storage object or null if not found/invalid
 */
const getFullStorage = (): DevToolsStorageType | null => {
	try {
		if (typeof window === 'undefined') {
			return null;
		}

		const stored = window.sessionStorage.getItem(DEV_TOOLS_STORAGE_KEY);
		if (!stored) {
			return null;
		}

		const parsed = JSON.parse(stored);

		if (isValidDevToolsStorage(parsed)) {
			return parsed;
		}

		// Try to migrate from old format (just MfeVersionsType)
		if (isValidMfeVersionsType(parsed)) {
			return {
				data: { mfeVersions: parsed },
				preferences: DEFAULT_PREFERENCES
			};
		}

		// eslint-disable-next-line no-console
		console.warn('Invalid dev tools data structure found in sessionStorage');
		return null;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Failed to retrieve dev tools data from sessionStorage:', error);
		return null;
	}
};

/**
 * Saves the full storage object to sessionStorage
 * @param storage - The storage object to save
 */
const saveFullStorage = (storage: DevToolsStorageType): void => {
	try {
		if (typeof window === 'undefined') {
			// eslint-disable-next-line no-console
			console.warn('Cannot save to sessionStorage: window is undefined');
			return;
		}

		window.sessionStorage.setItem(DEV_TOOLS_STORAGE_KEY, JSON.stringify(storage));
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Failed to save dev tools data to sessionStorage:', error);
	}
};

/**
 * Safely saves MFE version data to sessionStorage with error handling
 * @param versions - The MFE versions to save
 */
export const saveDevToolsStorage = (versions: MfeVersionsType): void => {
	const currentStorage = getFullStorage();
	const newStorage: DevToolsStorageType = {
		data: { mfeVersions: versions },
		preferences: currentStorage?.preferences || DEFAULT_PREFERENCES
	};
	saveFullStorage(newStorage);
};

/**
 * Safely retrieves MFE version data from sessionStorage with error handling
 * @returns The saved MFE versions or null if not found/invalid
 */
export const getDevToolsStorage = (): MfeVersionsType | null => {
	const storage = getFullStorage();
	return storage?.data.mfeVersions || null;
};

/**
 * Safely removes MFE version data from sessionStorage
 */
export const resetDevToolsStorage = (): void => {
	const currentStorage = getFullStorage();
	const newStorage: DevToolsStorageType = {
		data: { mfeVersions: DEFAULT_MFE_VERSIONS },
		preferences: currentStorage?.preferences || DEFAULT_PREFERENCES
	};
	saveFullStorage(newStorage);
};

/**
 * Safely retrieves preferences from sessionStorage
 * @returns The saved preferences or default preferences
 */
export const getDevToolsPreferences = (): DevToolsPreferences => {
	const storage = getFullStorage();
	// Merge with defaults to handle missing properties (e.g., position added later)
	return { ...DEFAULT_PREFERENCES, ...storage?.preferences };
};

/**
 * Safely saves preferences to sessionStorage
 * @param preferences - The preferences to save
 */
export const saveDevToolsPreferences = (preferences: DevToolsPreferences): void => {
	const currentStorage = getFullStorage();
	const newStorage: DevToolsStorageType = {
		data: currentStorage?.data || { mfeVersions: DEFAULT_MFE_VERSIONS },
		preferences
	};
	saveFullStorage(newStorage);
};

/**
 * Hides the dev tools by setting showDevTools to false
 */
export const hideDevTools = (): void => {
	const currentPreferences = getDevToolsPreferences();
	saveDevToolsPreferences({ ...currentPreferences, showDevTools: false });
};

/**
 * Shows the dev tools by setting showDevTools to true
 */
export const showDevTools = (): void => {
	const currentPreferences = getDevToolsPreferences();
	saveDevToolsPreferences({ ...currentPreferences, showDevTools: true });
};

/**
 * Query parameter names for MFE version overrides
 */
export const VERSION_QUERY_PARAMS = {
	auth: 'authVersion',
	settings: 'settingsVersion',
	garage: 'garageVersion'
} as const;

/**
 * Sanitizes version input to prevent XSS and limit length
 * @param input - The version string to sanitize
 * @returns Sanitized version string
 */
const sanitizeVersionInput = (input: string): string => {
	return input.replace(/[<>'&]/g, '').slice(0, 20);
};

/**
 * Parses URL query parameters for MFE version overrides
 * @returns MfeVersionsType with versions from query params, or null if no params found
 */
export const getVersionsFromQueryParams = (): Partial<MfeVersionsType> | null => {
	if (typeof window === 'undefined') {
		return null;
	}

	try {
		const urlParams = new URLSearchParams(window.location.search);
		const authVersion = urlParams.get(VERSION_QUERY_PARAMS.auth);
		const settingsVersion = urlParams.get(VERSION_QUERY_PARAMS.settings);
		const garageVersion = urlParams.get(VERSION_QUERY_PARAMS.garage);

		// If no version params are present, return null
		if (!authVersion && !settingsVersion && !garageVersion) {
			return null;
		}

		const versions: Partial<MfeVersionsType> = {};

		if (authVersion) {
			versions.auth = { version: sanitizeVersionInput(authVersion) };
		}
		if (settingsVersion) {
			versions.settings = { version: sanitizeVersionInput(settingsVersion) };
		}
		if (garageVersion) {
			versions.garage = { version: sanitizeVersionInput(garageVersion) };
		}

		return versions;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Failed to parse version query parameters:', error);
		return null;
	}
};

/**
 * Removes version query parameters from the URL without triggering a page reload
 * This prevents re-application of versions on page reload after reset
 */
const removeVersionQueryParams = (): void => {
	if (typeof window === 'undefined') return;

	try {
		const url = new URL(window.location.href);
		url.searchParams.delete(VERSION_QUERY_PARAMS.auth);
		url.searchParams.delete(VERSION_QUERY_PARAMS.settings);
		url.searchParams.delete(VERSION_QUERY_PARAMS.garage);

		// Replace URL without reload
		window.history.replaceState({}, '', url.toString());
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Failed to remove version query params from URL:', error);
	}
};

/**
 * Applies URL query parameter version overrides to storage
 * This should be called early in the app lifecycle
 * @returns True if query params were applied, false otherwise
 */
export const applyQueryParamVersions = (): boolean => {
	const queryVersions = getVersionsFromQueryParams();

	if (!queryVersions) {
		return false;
	}

	const currentVersions = getDevToolsStorage() || DEFAULT_MFE_VERSIONS;

	const mergedVersions: MfeVersionsType = {
		auth: queryVersions.auth || currentVersions.auth,
		settings: queryVersions.settings || currentVersions.settings,
		garage: queryVersions.garage || currentVersions.garage
	};

	saveDevToolsStorage(mergedVersions);

	// Remove version query params from URL to prevent re-application on reload
	removeVersionQueryParams();

	return true;
};
